import { useEffect } from 'react';
import { RefreshCcw } from 'lucide-react';
import { useRouteError } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CustomErrorView } from '../../components/custom-error-view/custom-error-view';

const unavailable = '/assets/images/unavailable.svg';

/**
 * Route-level `errorElement` for the catch-all data route in `App.tsx`.
 *
 * Without one, React Router falls back to its own developer screen ("Unexpected
 * Application Error!" plus a raw stack), which is what users see today whenever any
 * render or commit throws -- including transient DOM errors such as
 * `Failed to execute 'removeChild' on 'Node'`, which browser translation extensions and
 * other page-mutating extensions can trigger inside React's commit phase.
 *
 * This renders the app's own error view instead and offers a reload, which recovers the
 * session in those transient cases. The error is still logged so it stays diagnosable.
 */
export const RouteErrorPage = () => {
  const { t } = useTranslation();
  const error = useRouteError();

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error('Unhandled route error:', error);
  }, [error]);

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <CustomErrorView
      image={unavailable}
      title={t('SORRY_SOMETHING_WENT_WRONG', { defaultValue: 'Sorry! Something Went Wrong' })}
      description={t('PAGE_TEMPORARILY_UNAVAILABLE', {
        defaultValue: 'This page is temporarily unavailable.',
      })}
      buttonText={t('RELOAD_PAGE', { defaultValue: 'Reload Page' })}
      buttonIcon={<RefreshCcw />}
      onButtonClick={handleReload}
    />
  );
};
